// @flow
import * as at from './action-types/new-order';
import * as catalogApi from '../modules/api/catalog';
import type { Dispatch } from '../store';
import type { Customer } from '../modules/customer';
import type {
  AbstractCatalog,
  AbstractCatalogFilter,
} from '../modules/abstract-catalog';
import type { User } from '../modules/user';
import type { TabularOrder } from '../modules/tabular-order';
import { tabularOrder } from '../modules/offline-order-form';

import { fsa, type FSA } from 'modules/fsa';

export type KeywordFilterSet = FSA<
  typeof at.NEW_ORDER_KEYWORD_FILTER_SET,
  string,
>;

export type CatalogTypeFilterSet = FSA<
  typeof at.NEW_ORDER_CATALOG_TYPE_FILTER_SET,
  AbstractCatalogFilter,
>;

export type CustomerSet = FSA<typeof at.NEW_ORDER_CUSTOMER_SET, Customer>;

export type DetailsOpen = FSA<
  typeof at.NEW_ORDER_DETAILS_OPEN,
  AbstractCatalog,
>;

export type DetailsClose = FSA<typeof at.NEW_ORDER_DETAILS_CLOSE>;

export type ExportAvailabilityOpen = FSA<
  typeof at.NEW_ORDER_EXPORT_AVAILABILITY_OPEN,
  AbstractCatalog,
>;

export type ExportAvailabilityPayload = $ReadOnly<{|
  catalog: AbstractCatalog,
  customer: Customer,
  date: Date,
  futureAvailableData?: boolean,
|}>;

export type ExportAvailability = FSA<
  typeof at.NEW_ORDER_EXPORT_AVAILABILITY,
  ExportAvailabilityPayload,
>;

export type ExportAvailabilityClose = FSA<
  typeof at.NEW_ORDER_EXPORT_AVAILABILITY_CLOSE,
>;

export type OfflineOrderFormReceive = FSA<
  typeof at.NEW_ORDER_OFFLINE_ORDER_FORM_RECEIVE,
  $ReadOnly<{|
    offlineOrderForm: TabularOrder,
    user: User,
  |}>,
>;

export type OfflineOrderFormFailToReceive = FSA<
  typeof at.NEW_ORDER_OFFLINE_ORDER_FORM_FAIL_TO_RECEIVE,
>;

export type OfflineOrderFormClear = FSA<
  typeof at.NEW_ORDER_OFFLINE_ORDER_FORM_CLEAR,
>;

export type Reset = FSA<typeof at.NEW_ORDER_RESET>;

export type Action =
  | KeywordFilterSet
  | CatalogTypeFilterSet
  | CustomerSet
  | DetailsOpen
  | DetailsClose
  | ExportAvailabilityOpen
  | ExportAvailability
  | ExportAvailabilityClose
  | OfflineOrderFormReceive
  | OfflineOrderFormFailToReceive
  | OfflineOrderFormClear
  | Reset;

export const keywordFilterSet = (payload: string): KeywordFilterSet =>
  fsa(at.NEW_ORDER_KEYWORD_FILTER_SET, payload);

export const catalogTypeFilterSet = (
  payload: AbstractCatalogFilter,
): CatalogTypeFilterSet => fsa(at.NEW_ORDER_CATALOG_TYPE_FILTER_SET, payload);

export const detailsOpen = (payload: AbstractCatalog): DetailsOpen =>
  fsa(at.NEW_ORDER_DETAILS_OPEN, payload);

export const detailsClose = (): DetailsClose => fsa(at.NEW_ORDER_DETAILS_CLOSE);

export const exportAvailabilityOpen = (
  payload: AbstractCatalog,
): ExportAvailabilityOpen =>
  fsa(at.NEW_ORDER_EXPORT_AVAILABILITY_OPEN, payload);

export const exportAvailability =
  (payload: ExportAvailabilityPayload): (() => void) =>
  (): void => {
    catalogApi.exportAvailability(payload);
  };

export const exportAvailabilityClose = (): ExportAvailabilityClose =>
  fsa(at.NEW_ORDER_EXPORT_AVAILABILITY_CLOSE);

export const customerSet = (payload: Customer): CustomerSet =>
  fsa(at.NEW_ORDER_CUSTOMER_SET, payload);

export const offlineOrderFormReceive = (
  payload: $ReadOnly<{|
    offlineOrderForm: TabularOrder,
    user: User,
  |}>,
): OfflineOrderFormReceive =>
  fsa(at.NEW_ORDER_OFFLINE_ORDER_FORM_RECEIVE, payload);

export const offlineOrderFormFailToReceive =
  (): OfflineOrderFormFailToReceive =>
    fsa(at.NEW_ORDER_OFFLINE_ORDER_FORM_FAIL_TO_RECEIVE);

export const offlineOrderFormSet =
  (payload: {
    file: File,
    user: User,
  }): ((dispatch: Dispatch) => Promise<mixed>) =>
  (dispatch: Dispatch) =>
    tabularOrder(payload.file)
      .then((olof) =>
        dispatch(
          offlineOrderFormReceive({
            offlineOrderForm: olof,
            user: payload.user,
          }),
        ),
      )
      .catch(() => dispatch(offlineOrderFormFailToReceive()));

export const offlineOrderFormClear = (): OfflineOrderFormClear =>
  fsa(at.NEW_ORDER_OFFLINE_ORDER_FORM_CLEAR);

export const reset = (): Reset => fsa(at.NEW_ORDER_RESET);
